import React from "react";
import { useTranslation } from "react-i18next";
import { Page } from "../Page";
import { Avatar } from "../Avatar";
import { SecondaryButton, PrimaryButton } from "../Button";
import { Menu, MenuActionItem } from "../Menu";
import { Textfield } from "../FormElements/Textfield";
import { PersonField } from "../PersonField";
import { IconPencil } from "../icons";
import { translationText } from "../i18n";
import { Editor, useEditor } from "../RichEditor";
import { RichEditorHandlers } from "../RichEditor/useEditor";

export namespace ProfileEditPage {
  export interface Person {
    id: string;
    fullName: string;
    avatarUrl: string | null;
    title?: string;
    profileLink?: string;
  }

  export interface Props {
    // Person data
    person: Person;

    // Form fields
    fullName: string;
    title: string;
    aboutMe: any;
    manager: Person | null;

    // Form handlers
    onFullNameChange: (value: string) => void;
    onTitleChange: (value: string) => void;
    onAboutMeChange: (value: any) => void;
    onManagerChange: (person: Person | null) => void;
    onSubmit: () => Promise<void>;
    onCancel?: () => void;

    // Avatar management
    onAvatarUpload?: (file: File) => Promise<void>;
    onAvatarRemove?: () => Promise<void>;
    avatarUploading?: boolean;
    avatarUploadProgress?: number | null;
    avatarError?: string | null;
    canChangeAvatar?: boolean;

    // Manager search
    managerSearch: PersonField.SearchData;

    // Rich text handlers
    richTextHandlers: RichEditorHandlers;
    localDraftKeyBase?: string;

    // Options
    isCurrentUser: boolean;

    // Navigation paths
    fromLocation: string | null;
    companyAdminPath: string;
    managePeoplePath: string;
    homePath: string;

    // State
    isSubmitting?: boolean;
  }
}

export function ProfileEditPage(props: ProfileEditPage.Props) {
  const { t } = useTranslation();
  const managerLabel = props.isCurrentUser ? t("Who is your manager?") : t("Who is their manager?");

  const navigation = React.useMemo(() => {
    if (props.fromLocation === "admin-manage-people") {
      return [
        { label: t("Company Administration"), to: props.companyAdminPath },
        { label: t("Manage Team Members"), to: props.managePeoplePath },
      ];
    } else {
      return [{ label: t("Home"), to: props.homePath }];
    }
  }, [props.fromLocation, props.companyAdminPath, props.managePeoplePath, props.homePath, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await props.onSubmit();
  };

  return (
    <Page title={translationText(t("Edit Profile"))} size="small" navigation={navigation} testId="profile-edit-page">
      <div className="p-8">
        <form onSubmit={handleSubmit}>
          <AvatarSection {...props} />

          {/* Form Fields */}
          <div className="space-y-4">
            <Textfield
              label={translationText(t("Name"))}
              value={props.fullName}
              onChange={(e) => props.onFullNameChange(e.target.value)}
              testId="name"
              required
            />

            <Textfield
              label={translationText(t("Title in Company"))}
              value={props.title}
              onChange={(e) => props.onTitleChange(e.target.value)}
              testId="title"
            />

            {props.isCurrentUser && (
              <div data-test-id="about-me">
                <label className="font-bold text-sm mb-1 block">{t("About me")}</label>
                <AboutMeEditor
                  value={props.aboutMe}
                  onChange={props.onAboutMeChange}
                  handlers={props.richTextHandlers}
                  localDraftKey={props.localDraftKeyBase ? `${props.localDraftKeyBase}:about-me` : undefined}
                />
              </div>
            )}

            {/* Manager Section */}
            <div>
              <label className="font-bold text-sm mb-1 block">{managerLabel}</label>
              <PersonField
                person={props.manager}
                setPerson={props.onManagerChange}
                searchData={props.managerSearch}
                testId="manager"
                variant="form-field"
                emptyStateMessage={translationText(t("Select manager"))}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-6 flex gap-2">
            <PrimaryButton type="submit" loading={props.isSubmitting} testId="submit">
              {t("Save Changes")}
            </PrimaryButton>

            {props.onCancel && (
              <SecondaryButton type="button" onClick={props.onCancel} disabled={props.isSubmitting}>
                {t("Cancel")}
              </SecondaryButton>
            )}
          </div>
        </form>
      </div>
    </Page>
  );
}

function AboutMeEditor({
  value,
  onChange,
  handlers,
  localDraftKey,
}: {
  value: any;
  onChange: (value: any) => void;
  handlers: RichEditorHandlers;
  localDraftKey?: string;
}) {
  const { t } = useTranslation();
  const editor = useEditor({
    handlers,
    placeholder: translationText(t("Share a short bio, what you work on, or anything you'd like others to know.")),
    onUpdate: ({ json }) => onChange(json),
    content: value,
    localDraft: { key: localDraftKey },
  });

  React.useEffect(() => {
    if (editor.editor && !editor.localDraftRestored) {
      editor.editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor.editor, editor.localDraftRestored]);

  React.useEffect(() => {
    if (!editor.editor || !editor.localDraftRestored) return;

    onChange(editor.editor.getJSON());
  }, [editor.editor, editor.localDraftRestored]);

  return <Editor editor={editor} compactToolbar />;
}

function AvatarSection(props: ProfileEditPage.Props) {
  const { t } = useTranslation();
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const handleFileSelect = React.useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !props.onAvatarUpload) return;

      await props.onAvatarUpload(file);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [props.onAvatarUpload],
  );
  const handleChangePhotoClick = React.useCallback(() => {
    if (props.avatarUploading) return;
    handleFileSelect();
  }, [props.avatarUploading, handleFileSelect]);

  const handleRemovePhotoClick = React.useCallback(() => {
    if (props.avatarUploading) return;
    props.onAvatarRemove?.();
  }, [props.avatarUploading, props.onAvatarRemove]);

  const previewPerson = {
    ...props.person,
    avatarUrl: props.person.avatarUrl ?? "",
  };

  return (
    <section className="flex flex-col w-full justify-center items-center text-center mb-8">
      <div className="relative inline-block">
        <Avatar person={previewPerson} size="xxlarge" />

        {props.canChangeAvatar && (
          <Menu
            size="tiny"
            testId="profile-avatar-menu"
            customTrigger={
              <div className="absolute bottom-2 -right-7 opacity-85 hover:opacity-100 transition-all duration-200 focus:outline-none">
                <div className="flex items-center gap-0.5 text-xs text-content-dimmed cursor-pointer">
                  <IconPencil size={16} />
                  {t("Edit")}
                </div>
              </div>
            }
          >
            <MenuActionItem onClick={handleChangePhotoClick} testId="profile-avatar-menu-change">
              {t("Change photo")}
            </MenuActionItem>
            <MenuActionItem
              onClick={handleRemovePhotoClick}
              danger
              hidden={!props.person.avatarUrl}
              testId="profile-avatar-menu-remove"
            >
              {t("Remove photo")}
            </MenuActionItem>
          </Menu>
        )}
      </div>

      {props.canChangeAvatar && (props.avatarUploading || props.avatarError) && (
        <div className="mt-4 space-y-2">
          {props.avatarUploading && (
            <p className="text-sm text-content-dimmed">
              {props.avatarUploadProgress !== null
                ? t("Uploading {{progress}}%", { progress: props.avatarUploadProgress })
                : t("Saving...")}
            </p>
          )}

          {props.avatarError && <p className="text-sm text-callout-error-content">{props.avatarError}</p>}
        </div>
      )}

      {props.canChangeAvatar && (
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
      )}
    </section>
  );
}
